// src/components/operationCenter/widgets/HeatmapWidget.tsx
// Widget wrapper for HeatmapChart from TokenScreener

import React, { useMemo, useState, useCallback, useEffect } from "react";
import type { WidgetProps, HeatmapConfig } from "@/types/dashboard";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useMetricsCatalog } from "@/hooks/useMetricsCatalog";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import { useTokenCategoryMap } from "@/hooks/tokens/compare/useCompareData";
import { filterTokenRows } from "@/utils/tokens";
import { TimeRangePicker } from "@/components/common";
import { MetricKeys } from "@/constants/metrics";
import HeatmapChart from "@/components/tokenScreener/charts/HeatmapChart";
import type { MetricInfo } from "@/types/metrics";
import type { CategoryTreeNode } from "@/types/dashboards/categories";
import type { TokenRow } from "@/types/movers";

type RangeKey = '7d' | '30d' | '90d' | '365d';
type TransformType = 'raw' | 'momentum' | 'vol' | 'dominance';
type WindowDays = 7 | 30 | 90;

interface MetricOption {
  key: string;
  label: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  { key: MetricKeys.PriceClose, label: "Price" },
  { key: MetricKeys.Volume, label: "Volume" },
  { key: MetricKeys.TVL, label: "TVL" },
  { key: MetricKeys.MarketCap, label: "Market Cap" },
  { key: "futures_fr_avg", label: "Funding Rate" },
];

const TRANSFORM_OPTIONS: { key: TransformType; label: string }[] = [
  { key: 'raw', label: 'Raw' },
  { key: 'momentum', label: 'Momentum' },
  { key: 'vol', label: 'Volatility' },
  { key: 'dominance', label: 'Dominance' },
];

// Set of valid metric keys for quick lookup
const VALID_METRIC_KEYS = new Set(METRIC_OPTIONS.map(m => m.key));

// Validate and normalize metric key
const getValidMetricKey = (configValue: string | undefined, defaultValue: string): string => {
  if (configValue && VALID_METRIC_KEYS.has(configValue)) {
    return configValue;
  }
  return defaultValue;
};

const HeatmapWidget: React.FC<WidgetProps<HeatmapConfig>> = ({ 
  widget,
  onConfigChange 
}) => {
  const config = widget.config as HeatmapConfig;

  // State - initialized from config with validation
  const [selectedMetric, setSelectedMetric] = useState(() => 
    getValidMetricKey(config.metric, MetricKeys.PriceClose)
  );
  const [range, setRange] = useState<RangeKey>((config.time_range as RangeKey) ?? '30d');
  const [transform, setTransform] = useState<TransformType>(config.transform ?? 'raw');
  const [windowDays, setWindowDays] = useState<WindowDays>(config.window_days ?? 7);
  const [clustered, setClustered] = useState(config.clustered ?? false);
  const initialCategory = useMemo(
    () => config.category ?? 'all',
    [config.category]
  );
  const [category, setCategory] = useState<string>(initialCategory);

  // Data fetching
  const { data: rows } = useTokensScreenerData();
  const { roots: categoryRoots, maps } = useCategoriesTree();
  const { data: tokenCategoryMap } = useTokenCategoryMap();

  // Metrics catalog
  const { data: catalog } = useMetricsCatalog();
  const catalogMap: Record<string, MetricInfo> = useMemo(() => {
    const map: Record<string, MetricInfo> = {};
    (catalog || []).forEach((m) => { 
      if ((m as MetricInfo)?.metric_key) map[m.metric_key] = m as MetricInfo; 
    });
    return map;
  }, [catalog]);

  // Label lookup with fallback
  const labelFromCatalog = useCallback((key: string): string => {
    const entry = catalogMap[key] as unknown as { display_name?: string | null; label?: string | null; name?: string | null } | undefined;
    return (entry?.display_name || entry?.label || entry?.name || METRIC_OPTIONS.find(m => m.key === key)?.label || key);
  }, [catalogMap]);

  // Filter rows by category
  const rowsFiltered = useMemo(() => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return filterTokenRows(rows as TokenRow[], {
      search: '',
      mcMin: '',
      mcMax: '',
      category,
      maps,
      tokenCategoryMap: (tokenCategoryMap as unknown as Record<string, number[]>) || {},
      excludeStables: true,
      getTicker: (r) => String(r["tokenSymbol"] ?? ''),
      getName: (r) => String(r["tokenName"] ?? r["name"] ?? ''),
      getMarketCap: (r) => (r["market_cap"] as number | null) ?? (r[MetricKeys.MarketCap] as number | null) ?? null,
    });
  }, [rows, category, maps, tokenCategoryMap]);

  // Get flat list of categories
  const categoryOptions = useMemo(() => {
    if (!categoryRoots) return [];
    const getLeaves = (node: CategoryTreeNode): CategoryTreeNode[] => {
      if (node.is_leaf) return [node];
      return (node.children || []).flatMap(getLeaves);
    };
    return categoryRoots
      .filter((c) => c.category_code !== 'overview')
      .flatMap((root) => getLeaves(root));
  }, [categoryRoots]);

  // Handlers
  const handleMetricChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const metric = e.target.value;
    setSelectedMetric(metric);
    onConfigChange?.({ metric });
  }, [onConfigChange]);

  const handleRangeChange = useCallback((newRange: RangeKey) => {
    setRange(newRange);
    onConfigChange?.({ time_range: newRange });
  }, [onConfigChange]);

  const handleTransformChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newTransform = e.target.value as TransformType;
    setTransform(newTransform);
    onConfigChange?.({ transform: newTransform });
  }, [onConfigChange]);

  const handleWindowDaysChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newWindow = Number(e.target.value) as WindowDays;
    setWindowDays(newWindow);
    onConfigChange?.({ window_days: newWindow });
  }, [onConfigChange]);

  const handleClusteredToggle = useCallback(() => {
    const newValue = !clustered;
    setClustered(newValue);
    onConfigChange?.({ clustered: newValue });
  }, [clustered, onConfigChange]);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    setCategory(next);
    onConfigChange?.({ category: next });
  }, [onConfigChange]);

  // Sync state with config when config changes externally
  useEffect(() => {
    if (config.metric) {
      const validMetric = getValidMetricKey(config.metric, MetricKeys.PriceClose);
      setSelectedMetric(validMetric);
    }
    if (config.time_range) {
      setRange(config.time_range as RangeKey);
    }
    if (config.transform) {
      setTransform(config.transform);
    }
    if (config.window_days) {
      setWindowDays(config.window_days);
    }
    if (config.clustered !== undefined) {
      setClustered(config.clustered);
    }
    if (config.category) {
      setCategory(config.category);
    }
  }, [config.metric, config.time_range, config.transform, config.window_days, config.clustered, config.category]);

  const metricLabel = useMemo(() => {
    return METRIC_OPTIONS.find((m) => m.key === selectedMetric)?.label ?? selectedMetric;
  }, [selectedMetric]);

  const categoryLabel = useMemo(() => {
    if (category === "all") return "All Tokens";
    const match = categoryOptions.find((cat) => cat.category_code === category);
    return match?.category_name ?? category;
  }, [category, categoryOptions]);

  const transformLabel = useMemo(() => {
    if (transform === "momentum") return `${metricLabel} Momentum`;
    if (transform === "vol") return `${metricLabel} Volatility`;
    if (transform === "dominance") return `${metricLabel} Dominance`;
    return metricLabel;
  }, [transform, metricLabel]);

  const chartTitle = useMemo(() => {
    return `Heatmap - ${transformLabel}, ${categoryLabel}`;
  }, [transformLabel, categoryLabel]);

  // Show window selector only for momentum/vol transforms
  const showWindowSelector = transform === 'momentum' || transform === 'vol';

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {chartTitle}
        </span>
      </div>
      {/* Controls */}
      <div className="flex-shrink-0 flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-700/30">
        {/* Metric selector */}
        <select
          value={selectedMetric}
          onChange={handleMetricChange}
          className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0"
        >
          {METRIC_OPTIONS.map(m => (
            <option key={m.key} value={m.key}>{m.label}</option>
          ))}
        </select>

        {/* Transform selector */}
        <select
          value={transform}
          onChange={handleTransformChange}
          className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0"
        >
          {TRANSFORM_OPTIONS.map(t => (
            <option key={t.key} value={t.key}>{t.label}</option>
          ))}
        </select>

        {/* Window selector (only for momentum/vol) */}
        {showWindowSelector && (
          <select
            value={windowDays}
            onChange={handleWindowDaysChange}
            className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0"
          >
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
          </select>
        )}

        {/* Category selector */}
        <select
          value={category}
          onChange={handleCategoryChange}
          className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0 max-w-[100px]"
        >
          <option value="all">All</option>
          {categoryOptions.map((cat) => (
            <option key={cat.category_code} value={cat.category_code}>
              {cat.category_name}
            </option>
          ))}
        </select>

        {/* Clustered toggle */}
        <button
          onClick={handleClusteredToggle}
          className={`px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors ${
            clustered 
              ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
              : 'bg-gray-800/50 text-slate-400 border border-gray-700/50 hover:text-slate-200'
          }`}
          title="Cluster similar tokens"
        >
          cluster
        </button>

        {/* Time range */}
        <div className="ml-auto">
          <TimeRangePicker
            options={['7d', '30d', '90d', '365d'] as const}
            value={range}
            onChange={(v) => handleRangeChange(v as RangeKey)}
            theme="blue"
          />
        </div>
      </div>

      {/* Chart container */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <HeatmapChart
          rows={rowsFiltered as Array<Record<string, unknown>>}
          range={range}
          xKey={selectedMetric}
          transform={transform}
          windowDays={windowDays}
          clustered={clustered}
          excludeStables={true}
          search=""
          mcMin=""
          mcMax=""
          labelFromCatalog={labelFromCatalog}
        />
      </div>
    </div>
  );
};

export default HeatmapWidget;
